<template>
  <!--
    relative, because the dish button below stretches to fill it.

    The hover fill has always been the whole row while only the text block was
    clickable, so the padding, the gap beside the artwork and the space under
    the price all lit up and then did nothing. A row that reacts to a pointer
    has promised something.
  -->
  <div class="relative -mx-3 h-menu-row border-t px-3 py-4 pj-tap-row hover:bg-surface-faint">
    <div class="flex items-start gap-3.5">
      <!-- after:inset-0 makes the whole row this button's target without
           nesting one button inside another, which is invalid, and without a
           click handler on a div, which is unreachable by keyboard. One button
           in the accessibility tree, named by the dish, covering the row. -->
      <button
        class="flex min-w-0 flex-1 flex-col items-start gap-1 text-left after:absolute after:inset-0"
        type="button"
        @click="emit('select', props.item)"
      >
        <span class="line-clamp-2 text-title text-ink">{{ props.item.name }}</span>

        <span
          v-if="props.item.description"
          class="line-clamp-1 text-caption text-muted"
        >
          {{ props.item.description }}
        </span>

        <!-- Under the description rather than in a column: this view trades the
             price column away for photography. The text view is where prices
             line up. -->
        <span class="mt-0.5 w-full truncate">
          <span
            v-if="isSoldOut"
            class="text-title text-muted"
            >Sold out</span
          >
          <span
            v-else
            class="text-title text-ink"
            >{{ priceLabel }}</span
          >
          <span
            v-if="venueHint"
            class="text-caption font-medium text-ink"
          >
            · {{ venueHint }}</span
          >
        </span>
      </button>

      <!-- Artwork only where a dish has actually been photographed. No tint, no
           placeholder, no reserved gap — nothing to the right of it depends on
           the row's width, so its absence costs no alignment. -->
      <!-- pointer-events-none: being `relative`, this box paints above the dish
           button's stretched target and swallowed every tap on the photo. Only
           the add button inside takes its own taps back. -->
      <div
        v-if="hasArt"
        class="pointer-events-none relative shrink-0"
      >
        <!-- Not a button any more. It sits inside the dish button's stretched
             target, so it is already clickable — a second control firing the
             same event only put the dish in the tab order twice. -->
        <div
          class="block h-[94px] w-[104px] overflow-hidden rounded-m bg-surface"
          :class="isSoldOut && 'opacity-45'"
        >
          <img
            v-if="!imageFailed"
            alt=""
            class="h-full w-full object-cover"
            decoding="async"
            height="94"
            loading="lazy"
            :src="props.item.thumbnailUrl ?? undefined"
            width="104"
            @error="imageFailed = true"
          />
        </div>

        <!--
          right-0 top-0, and those zeroes are what make the two variants line up
          rather than nearly line up.

          The artwork is the last thing in the row and the photoless + is the
          last thing in the row, and the row is items-start — so both boxes start
          at the same point. Pinning this one flush to that corner puts the two
          44px targets in identical positions, and the 30px chips they centre
          land on the same pixel whether a dish was photographed or not. Any
          inset here, however small, is the control moving as you scan down a
          menu that alternates between the two.

          Same fill as the + on a photoless row, on Mark's call. The system's
          raised variant is literal white, which survives a theme switch because a
          photograph does not — but repeated down a dark menu it reads as a row of
          bright dots. It keeps the chip shadow, which is the part that was
          actually doing the work of lifting it off an image.

          z-10 to sit above the dish button's stretched target, or adding would
          open the sheet instead.
        -->
        <button
          v-if="canAdd"
          :aria-label="addLabel"
          class="pointer-events-auto absolute right-0 top-0 z-10 flex h-tap w-tap items-center justify-center"
          type="button"
          @click="emit('add', props.item)"
        >
          <span
            class="flex h-[30px] w-[30px] items-center justify-center rounded-full pj-tap"
            :class="hasCount ? 'bg-primary text-on-primary' : 'bg-surface text-ink shadow-chip'"
          >
            <span
              v-if="hasCount"
              class="pj-num text-item"
              >{{ props.count }}</span
            >
            <PjIcon
              v-else
              :name="PJ_ICON.ADD"
            />
          </span>
        </button>
      </div>

      <!-- No photograph: the add moves to the row's edge and the text runs full
           width. This is the state that makes the view work on a menu where
           half the dishes were never shot. -->
      <button
        v-else-if="canAdd"
        :aria-label="addLabel"
        class="relative z-10 flex h-tap w-tap shrink-0 items-center justify-center"
        type="button"
        @click="emit('add', props.item)"
      >
        <span
          class="flex h-[30px] w-[30px] items-center justify-center rounded-full pj-tap"
          :class="hasCount ? 'bg-primary text-on-primary' : 'bg-surface text-ink'"
        >
          <span
            v-if="hasCount"
            class="pj-num text-item"
            >{{ props.count }}</span
          >
          <PjIcon
            v-else
            :name="PJ_ICON.ADD"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import type {
  PjMenuRowEmits,
  PjMenuRowProps
} from '@/domains/pjazza/components/molecules/pj-menu-row/PjMenuRow.types.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import { formatMenuItemPrice } from '@/domains/pjazza/utils/formatMenuItemPrice.ts'

/**
 * The default menu row: name, description, price, and large artwork on the
 * right where it exists.
 *
 * This replaced the square tile grid. The grid's tint system was a fine answer
 * to the occasional missing photograph and a bad one at scale — on a 278-item
 * menu with 150 photoless dishes it stopped being a treatment and became the
 * look of the menu. A row simply omits the artwork instead, which is both
 * honest and less work for the eye.
 */
const props = defineProps<PjMenuRowProps>()

const emit = defineEmits<PjMenuRowEmits>()

// A failed image hides itself and leaves the surface-coloured box standing.
// Collapsing the box instead would reflow the row the moment a slow or broken
// URL resolved — the data says this dish has a photograph, so the space is
// reserved on that basis rather than on whether it loaded.
const imageFailed = ref(false)

watch(
  () => props.item.id,
  () => (imageFailed.value = false)
)

const hasArt = computed(() => props.item.thumbnailUrl !== null)

const isSoldOut = computed(() => !props.item.available)

const canAdd = computed(() => !isSoldOut.value && props.item.isOrderable)

/** Shown only on a dish in its schedule that the diner still cannot add here. */
const venueHint = computed(() => (isSoldOut.value || props.item.isOrderable ? null : props.inVenueHint))

const priceLabel = computed(() => formatMenuItemPrice(props.item))

const hasCount = computed(() => (props.count ?? 0) > 0)

const addLabel = computed(() =>
  hasCount.value ? `${props.item.name}, ${props.count} in your order` : `Add ${props.item.name}`
)
</script>
